<template>
  <coozzy-card data-cy="application-form-section-preferred-deposit">
    <div class="row">
      <div class="col-12">
        <h5
          class="mb-3"
          data-cy="application-form-preferred-deposit-title">
          {{ $t('message.onBoarding.buildings.objects.deposit.amount') }}: {{ $options.filters.formatPrice(object.deposit.amount, 'CHF') }}
        </h5>
      </div>
      <div class="col-12">
        <p>
          <label>{{ $t('message.onBoarding.buildings.objects.deposit.accountOffers', { accountName: marketingAccount?.company ? marketingAccount.company.name : marketingAccount?.individual ? marketingAccount.individual.name : '' }) }}</label>
        </p>
        {{ $t('message.onBoarding.buildings.objects.deposit.pleaseSelectOption') }}
      </div>
      <div
        v-if="!object"
        class="col-12 text-center">
        <coozzy-spinner />
      </div>
      <template v-else>
        <div class="col">
          <div
            v-if="object.deposit.cashPaymentAccepted"
            class="row details-row-bg-2 mb-2">
            <div
              class="col-auto d-flex justify-content-center align-items-center">
              <coozzy-form-radio
                v-model="preferredDepositType"
                data-cy="application-form-preferred-deposit-cash-payment-radio"
                name="preferred-deposit-type"
                :default-model="preferredDepositType"
                :select-value="'PREFERRED_DEPOSIT_TYPE_CASH_PAYMENT'">
                {{ $t('message.onBoarding.buildings.objects.tenancies.deposit.type.DEPOSIT_TYPE_CASH_PAYMENT') }}
              </coozzy-form-radio>
            </div>
          </div>
          <div
            v-if="object.deposit.insuranceAccepted"
            class="row details-row-bg-1 mb-2">
            <div
              class="col-auto d-flex justify-content-center align-items-center">
              <coozzy-form-radio
                v-model="preferredDepositType"
                data-cy="application-form-preferred-deposit-insurance-radio"
                name="preferred-deposit-type"
                :default-model="preferredDepositType"
                :select-value="'PREFERRED_DEPOSIT_TYPE_INSURANCE'">
                {{ $t('message.onBoarding.buildings.objects.tenancies.deposit.type.DEPOSIT_TYPE_INSURANCE_GUARANTEE') }}
              </coozzy-form-radio>
            </div>
            <template
              v-if="object.deposit.insuranceNames.length > 0">
              <div
                class="col-12 pl-5">
                <template
                  v-for="(item, index) in object.deposit.insuranceNames">
                  <coozzy-form-radio
                    :key="index"
                    v-model="insuranceNames"
                    data-cy="application-form-preferred-deposit-insurance-choice-radio"
                    :default-model="insuranceNames"
                    name="deposit-insurance-choice"
                    :select-value="item">
                    {{ $t('message.onBoarding.buildings.objects.deposit.depositInsurance.' + item) }}
                  </coozzy-form-radio>
                </template>
              </div>
            </template>
          </div>
          <div
            v-if="object.deposit.bankDepotAccepted"
            class="row details-row-bg-2 mb-2">
            <div
              class="col-auto d-flex justify-content-center align-items-center">
              <coozzy-form-radio
                v-model="preferredDepositType"
                data-cy="application-form-preferred-deposit-depot-radio"
                name="preferred-deposit-type"
                :default-model="preferredDepositType"
                :select-value="'PREFERRED_DEPOSIT_TYPE_DEPOT'">
                {{ $t('message.onBoarding.buildings.objects.tenancies.deposit.type.DEPOSIT_TYPE_DEPOT') }}
              </coozzy-form-radio>
            </div>
            <template
              v-if="object.deposit.bankDepotNames.length > 0">
              <div
                class="col-12 pl-5">
                <template
                  v-for="(item, index) in uniqueDepositBankEntries(object.deposit.bankDepotNames)">
                  <coozzy-form-radio
                    :key="index"
                    v-model="bankDepotNames"
                    data-cy="application-form-preferred-deposit-bank-depot-choice-radio"
                    :default-model="bankDepotNames"
                    name="deposit-bank-depot-choice"
                    :select-value="item">
                    {{ item }}
                  </coozzy-form-radio>
                </template>
              </div>
            </template>
          </div>
          <div
            v-if="object.deposit.cooperativeShareAccepted"
            class="row details-row-bg-1 mb-2">
            <div
              class="col-auto d-flex justify-content-center align-items-center">
              <coozzy-form-radio
                v-model="preferredDepositType"
                data-cy="application-form-preferred-deposit-cooperative-share-radio"
                name="preferred-deposit-type"
                :default-model="preferredDepositType"
                :select-value="'PREFERRED_DEPOSIT_TYPE_COOPERATIVE_SHARE'">
                {{ $t('message.onBoarding.buildings.objects.tenancies.deposit.type.DEPOSIT_TYPE_COOPERATIVE_SHARE') }}
              </coozzy-form-radio>
            </div>
          </div>
          <div
            v-if="object.deposit.bankGuaranteeAccepted"
            class="row details-row-bg-2 mb-2">
            <div
              class="col-auto d-flex justify-content-center align-items-center">
              <coozzy-form-radio
                v-model="preferredDepositType"
                data-cy="application-form-preferred-deposit-bank-guarantee-radio"
                name="preferred-deposit-type"
                :default-model="preferredDepositType"
                :select-value="'PREFERRED_DEPOSIT_TYPE_BANK_GUARANTEE'">
                {{ $t('message.onBoarding.buildings.objects.tenancies.deposit.type.DEPOSIT_TYPE_BANK_GUARANTEE') }}
              </coozzy-form-radio>
            </div>
            <template
              v-if="object.deposit.bankGuaranteeNames.length > 0">
              <div
                class="col-12 pl-5">
                <template
                  v-for="(item, index) in uniqueDepositBankEntries(object.deposit.bankGuaranteeNames)">
                  <coozzy-form-radio
                    :key="index"
                    v-model="bankGuaranteeNames"
                    data-cy="application-form-preferred-deposit-bank-guarantee-choice-radio"
                    name="deposit-bank-guarantee-choice"
                    :default-model="bankGuaranteeNames"
                    :select-value="item">
                    {{ item }}
                  </coozzy-form-radio>
                </template>
              </div>
            </template>
          </div>
        </div>
      </template>
    </div>
  </coozzy-card>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue'
import CoozzyCard from '@/framework/components/card/CoozzyCard.vue'
import CoozzySpinner from '@/framework/components/misc/CoozzySpinner.vue'
import { mapMutations } from 'vuex'
import { V2PreferredDepositType } from '@/types/api'
import {
  ApplicationFormCompany,
  ApplicationFormIndividual,
  ApplicationFormPreferredDepositMarketingAccount,
  ApplicationFormPreferredDepositObject
} from '@/types/models'

/** Bank depot / guarantee options may be plain strings or `{ iid }` objects; dedupe by stable key. */
function uniqueDepositBankEntries(
  entries: ApplicationFormPreferredDepositObject['deposit']['bankDepotNames']
): ApplicationFormPreferredDepositObject['deposit']['bankDepotNames'] {
  return entries.filter((v, i, a) => {
    const vKey = typeof v === 'string' ? v : v.iid
    return a.findIndex(t => (typeof t === 'string' ? t : t.iid) === vKey) === i
  })
}

export default Vue.extend({
  name: 'ApplicationFormPreferredDeposit',
  components: {
    CoozzySpinner,
    CoozzyCard
  },
  props: {
    object: {
      type: Object as PropType<ApplicationFormPreferredDepositObject | null>,
      default: null
    },
    individual: {
      type: Object as PropType<ApplicationFormIndividual | null>,
      default: null
    },
    company: {
      type: Object as PropType<ApplicationFormCompany | null>,
      default: null
    },
    marketingAccount: {
      type: Object as PropType<ApplicationFormPreferredDepositMarketingAccount | null>,
      default: null
    }
  },
  computed: {
    preferredDepositType: {
      get(): V2PreferredDepositType | null {
        if (this.company) {
          return this.company.preferredDeposit.type === V2PreferredDepositType.PREFERRED_DEPOSIT_TYPE_UNDEFINED
            ? null
            : this.company.preferredDeposit.type
        }
        if (this.individual) {
          return this.individual.preferredDeposit.type === V2PreferredDepositType.PREFERRED_DEPOSIT_TYPE_UNDEFINED
            ? null
            : this.individual.preferredDeposit.type
        }
        return null
      },
      set(value: V2PreferredDepositType | null) {
        if (this.company) {
          const com = this.company
          com.preferredDeposit.type = value ?? V2PreferredDepositType.PREFERRED_DEPOSIT_TYPE_UNDEFINED
          this.updateCompany(com)
        } else if (this.individual) {
          const ind = this.individual
          ind.preferredDeposit.type = value ?? V2PreferredDepositType.PREFERRED_DEPOSIT_TYPE_UNDEFINED
          this.updateIndividual(ind)
        }
      }
    },
    insuranceNames: {
      get(): string {
        return (
          this.individual?.preferredDeposit?.insuranceNames?.toString() ||
          this.company?.preferredDeposit?.insuranceNames?.toString() ||
          ''
        )
      },
      set(value: string) {
        if (this.company) {
          const com = this.company
          com.preferredDeposit.insuranceNames = value ? [value] : []
          this.updateCompany(com)
        } else if (this.individual) {
          const ind = this.individual
          ind.preferredDeposit.insuranceNames = value ? [value] : []
          this.updateIndividual(ind)
        }
      }
    },
    bankDepotNames: {
      get(): string {
        return (
          this.individual?.preferredDeposit?.bankDepotNames?.toString() ||
          this.company?.preferredDeposit?.bankDepotNames?.toString() ||
          ''
        )
      },
      set(value: string) {
        const obj = this.object
        if (!obj) return
        if (this.company) {
          const com = this.company
          if (value) {
            com.preferredDeposit.bankDepotNames = [value]
            const idx = obj.deposit.bankDepotNames.findIndex(x => x === value)
            if (idx !== -1) {
              com.preferredDeposit.bankDepotIds = [obj.deposit.bankDepotIds[idx]]
            }
          } else {
            com.preferredDeposit.bankDepotNames = []
            com.preferredDeposit.bankDepotIds = []
          }
          this.updateCompany(com)
        } else if (this.individual) {
          const ind = this.individual
          if (value) {
            ind.preferredDeposit.bankDepotNames = [value]
            const idx = obj.deposit.bankDepotNames.findIndex(x => x === value)
            if (idx !== -1) {
              ind.preferredDeposit.bankDepotIds = [obj.deposit.bankDepotIds[idx]]
            }
          } else {
            ind.preferredDeposit.bankDepotNames = []
            ind.preferredDeposit.bankDepotIds = []
          }
          this.updateIndividual(ind)
        }
      }
    },
    bankGuaranteeNames: {
      get(): string {
        return (
          this.individual?.preferredDeposit?.bankGuaranteeNames?.toString() ||
          this.company?.preferredDeposit?.bankGuaranteeNames?.toString() ||
          ''
        )
      },
      set(value: string) {
        const obj = this.object
        if (!obj) return
        if (this.company) {
          const com = this.company
          com.preferredDeposit.bankGuaranteeNames = [value]
          const idx = obj.deposit.bankGuaranteeNames.findIndex(x => x === value)
          if (idx !== -1) {
            com.preferredDeposit.bankGuaranteeIds = [obj.deposit.bankGuaranteeIds[idx]]
          }
          this.updateCompany(com)
        } else if (this.individual) {
          const ind = this.individual
          if (value) {
            ind.preferredDeposit.bankGuaranteeNames = [value]
            const idx = obj.deposit.bankGuaranteeNames.findIndex(x => x === value)
            if (idx !== -1) {
              ind.preferredDeposit.bankGuaranteeIds = [obj.deposit.bankGuaranteeIds[idx]]
            }
          } else {
            ind.preferredDeposit.bankGuaranteeNames = []
            ind.preferredDeposit.bankGuaranteeIds = []
          }
          this.updateIndividual(ind)
        }
      }
    },
    collectiveInsuranceNames: {
      get(): string {
        return (
          this.individual?.preferredDeposit?.collectiveInsuranceNames?.toString() ||
          this.company?.preferredDeposit?.collectiveInsuranceNames?.toString() ||
          ''
        )
      },
      set(value: string) {
        if (this.company) {
          const com = this.company
          com.preferredDeposit.collectiveInsuranceNames = value ? [value] : []
          this.updateCompany(com)
        } else if (this.individual) {
          const ind = this.individual
          ind.preferredDeposit.collectiveInsuranceNames = value ? [value] : []
          this.updateIndividual(ind)
        }
      }
    },
    premiumPerYear: {
      get(): number | null {
        if (this.individual) {
          return this.individual.preferredDeposit.premiumPerYear !== -1
            ? this.individual.preferredDeposit.premiumPerYear
            : null
        }
        if (this.company) {
          return this.company.preferredDeposit.premiumPerYear !== -1
            ? this.company.preferredDeposit.premiumPerYear
            : null
        }
        return null
      },
      set(value: number | null) {
        const premiumOrSentinel = value ?? -1
        if (this.company) {
          const com = this.company
          com.preferredDeposit.premiumPerYear = premiumOrSentinel
          this.updateCompany(com)
        } else if (this.individual) {
          const ind = this.individual
          ind.preferredDeposit.premiumPerYear = premiumOrSentinel
          this.updateIndividual(ind)
        }
      }
    },
    premiumThisYear: {
      get(): number | null {
        if (this.individual) {
          return this.individual.preferredDeposit.premiumThisYear !== -1
            ? this.individual.preferredDeposit.premiumThisYear
            : null
        }
        if (this.company) {
          return this.company.preferredDeposit.premiumThisYear !== -1
            ? this.company.preferredDeposit.premiumThisYear
            : null
        }
        return null
      },
      set(value: number | null) {
        const premiumOrSentinel = value ?? -1
        if (this.company) {
          const com = this.company
          com.preferredDeposit.premiumThisYear = premiumOrSentinel
          this.updateCompany(com)
        } else if (this.individual) {
          const ind = this.individual
          ind.preferredDeposit.premiumThisYear = premiumOrSentinel
          this.updateIndividual(ind)
        }
      }
    }
  },
  watch: {
    preferredDepositType(newVal: V2PreferredDepositType | null) {
      if (this.company) {
        this.company.preferredDeposit.collectiveInsuranceNames = []
        this.company.preferredDeposit.bankDepotNames = []
        this.company.preferredDeposit.bankGuaranteeNames = []
        this.company.preferredDeposit.insuranceNames = []
      } else if (this.individual) {
        this.individual.preferredDeposit.collectiveInsuranceNames = []
        this.individual.preferredDeposit.bankDepotNames = []
        this.individual.preferredDeposit.bankGuaranteeNames = []
        this.individual.preferredDeposit.insuranceNames = []
      }
      if (newVal !== V2PreferredDepositType.PREFERRED_DEPOSIT_TYPE_INSURANCE) {
        this.premiumPerYear = null
        this.premiumThisYear = null
      }
    },
    collectiveInsuranceNames() {
      this.preferredDepositType = V2PreferredDepositType.PREFERRED_DEPOSIT_TYPE_COLLECTIVE_INSURANCE
    },
    bankGuaranteeNames() {
      this.preferredDepositType = V2PreferredDepositType.PREFERRED_DEPOSIT_TYPE_BANK_GUARANTEE
    },
    bankDepotNames() {
      this.preferredDepositType = V2PreferredDepositType.PREFERRED_DEPOSIT_TYPE_DEPOT
    },
    insuranceNames() {
      this.preferredDepositType = V2PreferredDepositType.PREFERRED_DEPOSIT_TYPE_INSURANCE
    }
  },
  methods: {
    ...mapMutations('applicationForm', ['updateIndividual', 'updateCompany']),
    uniqueDepositBankEntries,
    formatInsuranceNamesList(insuranceNames: string[]) {
      let text = ''
      for (let index = 0; index < insuranceNames.length; index++) {
        const element = insuranceNames[index]
        if (index > 0) {
          text += ', '
        }
        text += this.$t('message.onBoarding.buildings.objects.deposit.depositInsurance.' + element)
      }
      return text
    }
  }
})
</script>

<style lang="scss" scoped>
:deep(.custom-checkbox > label) {
  font-weight: normal;
}
</style>
